import { useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import { EmailEspAccount } from 'modules/graphql/types';
import { OpenNewWindow24 } from 'ui/icons';
import { BUTTON_SIZES } from 'ui/button';
import noop from 'utils/noop';
import {
  ARROW_DIRECTION_DOWN,
  CollapsibleContent,
  CollapsibleContentArrow,
} from 'ui/collapsibleContent';
import {
  ActionButton,
  ConnectLink,
  Wrapper,
  AccountDetails,
  AccountName,
  Main,
  LeftContent,
  RightContent,
  SectionHeading,
  ArrowButton,
} from '../AccountsSection.styled';
import { EmailAccountOverflowMenu } from './EmailAccountOverflowMenu';
import { EmailChannelRow } from './EmailChannelRow';
import { SendingPlatform } from '../../../constants';
import { useSendingPlatformsQueryContext } from '../../../contexts';
import {
  shouldShowAccountType,
  composeEmailAccountDetailsText,
  getSendingPlatformForEspAccount,
} from '../../../utils';

export const TEXT = {
  CONNECT_BUTTON: 'Connect to channels',
};

type Props = {
  espAccount: EmailEspAccount;
};

export const EmailAccount = ({ espAccount }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { selectEmailChannels, espDefinitions } = useSendingPlatformsQueryContext();

  const channels = selectEmailChannels(espAccount);

  const sendingPlatform = getSendingPlatformForEspAccount(espAccount) as SendingPlatform;

  const includeAccountType = shouldShowAccountType(sendingPlatform, espDefinitions);
  const idText = composeEmailAccountDetailsText(espAccount, includeAccountType);
  const { id, name } = espAccount;

  const channelTerm = 'connected channel';
  const collapseBtnText =
    channels.length === 1 ? `1 ${channelTerm}` : `${channels.length} ${channelTerm}s`;

  return (
    <Wrapper data-test={`Email-account-${kebabCase(name)}`}>
      <Main>
        <LeftContent>
          <AccountName>{name}</AccountName>
          <AccountDetails>{idText}</AccountDetails>
        </LeftContent>
        <RightContent>
          {channels.length > 0 ? (
            <ArrowButton onClick={() => setIsExpanded(!isExpanded)} size={BUTTON_SIZES.SMALL}>
              <span>{collapseBtnText}</span>
              <CollapsibleContentArrow
                isCollapsed={!isExpanded}
                arrowStart={ARROW_DIRECTION_DOWN}
                arrowEnd={180}
                width={16}
                height={16}
              />
            </ArrowButton>
          ) : (
            <ConnectLink href="/settings/channels" target="_blank">
              <ActionButton size={BUTTON_SIZES.LARGE} onClick={noop}>
                <span>{TEXT.CONNECT_BUTTON}</span>
                <OpenNewWindow24 />
              </ActionButton>
            </ConnectLink>
          )}
          <EmailAccountOverflowMenu espAccountId={id} />
        </RightContent>
      </Main>
      {channels.length > 0 && (
        <CollapsibleContent isCollapsed={!isExpanded}>
          <SectionHeading>{`${channelTerm}s`}</SectionHeading>
          {channels.map((channel) => (
            <EmailChannelRow key={channel.id} channel={channel} />
          ))}
        </CollapsibleContent>
      )}
    </Wrapper>
  );
};
